import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getInvoices } from '@/modules/Invoice/services'
import {
    INVOICE_STATUS_OPTIONS,
    INVOICE_TYPES,
} from '@/modules/Invoice/constants'
import { GetInvoicesRow } from '@/modules/Invoice/types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Typogaphy } from 'vnetwork-ui'
import { FileText, Receipt, ArrowRight } from 'lucide-react'
import { formatCurrency } from '@/utils/function'

const InvoicableWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()

    // Fetch invoicable invoices (cần xuất hoá đơn)
    const { data, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICES, 'invoicable-dashboard'],
        queryFn: () =>
            getInvoices({
                page: 1,
                page_size: 10,
                q: '',
                filters: btoa(
                    JSON.stringify({
                        invoicable: [true],
                        revenue_confirmed: [true],
                        invoice_type: [INVOICE_TYPES.Default],
                        invoice_status: [INVOICE_STATUS_OPTIONS.Approved],
                    })
                ),
            }),
    })

    const invoices = data?.results || []
    const total = data?.pagination?.total || 0

    const handleInvoiceClick = (invoiceId: number) => {
        navigate(
            generatePathDatacentral(paths.invoiceDetail, { id: invoiceId })
        )
    }

    const handleViewAll = () => {
        navigate(
            generatePathDatacentral(paths.invoices, {}, { tab: 'invoicable' })
        )
    }

    const renderInvoiceItem = (invoice: GetInvoicesRow) => {
        return (
            <div
                key={invoice.invoice_id}
                onClick={() => handleInvoiceClick(invoice.invoice_id)}
                className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="p-1.5 rounded-full flex-shrink-0 bg-green-100">
                        <Receipt className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-gray-900 truncate">
                            {invoice.customer?.customer_name ||
                                invoice.customer?.company_name}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                            {formatCurrency(invoice.invoice_total)}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                        {t('widgets.invoices.need_export', 'Cần xuất HĐ')}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                </div>
            </div>
        )
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Title */}
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                    <FileText className="w-5 h-5 text-green-500" />
                    <Typogaphy className="font-semibold text-primary">
                        {t(
                            'widgets.invoices.invoicable_title',
                            'Hoá đơn cần xuất'
                        )}
                    </Typogaphy>
                </div>

                {isLoading ? (
                    <div className="space-y-2 flex-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-14 rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Summary */}
                        <div
                            className="flex items-center gap-2 p-2.5 bg-green-50 rounded-lg mb-3 flex-shrink-0"
                            data-tour="invoicable-summary"
                        >
                            <Receipt className="w-4 h-4 text-green-600" />
                            <div>
                                <div className="text-lg font-bold text-green-600">
                                    {total}
                                </div>
                                <div className="text-[10px] text-green-600">
                                    {t(
                                        'widgets.invoices.invoices_need_export',
                                        'Hoá đơn cần xuất'
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="space-y-1.5 flex-1">
                            {invoices.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                    {t(
                                        'widgets.invoices.no_invoicable',
                                        'Không có hoá đơn cần xuất'
                                    )}
                                </div>
                            ) : (
                                <>
                                    {invoices
                                        .slice(0, 5)
                                        .map((invoice) =>
                                            renderInvoiceItem(invoice)
                                        )}
                                    {/* Empty placeholders */}
                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            5 - invoices.length
                                        ),
                                    }).map((_, i) => (
                                        <div
                                            key={`empty-${i}`}
                                            className="h-[52px] bg-gray-50 rounded-lg border border-dashed border-gray-200"
                                        />
                                    ))}
                                </>
                            )}
                        </div>

                        {/* View All */}
                        {total > 0 && (
                            <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                                <button
                                    onClick={handleViewAll}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {t('widgets.view_all')} ({total})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Paper>
    )
}

export default InvoicableWidget
